import type { BadgeTone, IconName } from "@sagot/ui";
import { syncPhase } from "../shared/sync";
import type { SourceView } from "../shared/types";

/** Table tabs (`All 24 | Files 14 | Websites 2 | Q&A 6 | Text 2`). */
export const SOURCE_TABS = ["all", "files", "websites", "qa", "text"] as const;
export type SourceTab = (typeof SOURCE_TABS)[number];
export type SourceKind = Exclude<SourceTab, "all">;

/** Type filter values; `file_type` values fold into these groups. */
export const TYPE_FILTERS = ["pdf", "spreadsheet", "word", "slides", "image", "website", "qa", "text"] as const;
export type TypeFilter = (typeof TYPE_FILTERS)[number];

const FILE_GROUP: Readonly<Record<string, TypeFilter>> = {
  pdf: "pdf",
  xlsx: "spreadsheet",
  csv: "spreadsheet",
  docx: "word",
  txt: "text",
  md: "text",
  pptx: "slides",
  png: "image",
  jpg: "image",
  jpeg: "image",
};

export const PAGE_SIZE = 25;

export function kindOf(source: Pick<SourceView, "type">): SourceKind {
  if (source.type === "website") return "websites";
  if (source.type === "qa") return "qa";
  if (source.type === "text") return "text";
  return "files";
}

export function typeOf(source: Pick<SourceView, "type" | "file_type">): TypeFilter {
  const kind = kindOf(source);
  if (kind === "websites") return "website";
  if (kind === "qa" || kind === "text") return kind;
  return FILE_GROUP[source.file_type ?? ""] ?? "pdf";
}

export function tabCounts(sources: readonly SourceView[]): Record<SourceTab, number> {
  const counts: Record<SourceTab, number> = { all: sources.length, files: 0, websites: 0, qa: 0, text: 0 };
  for (const source of sources) counts[kindOf(source)] += 1;
  return counts;
}

export interface SourceFilters {
  tab: SourceTab;
  query: string;
  type: TypeFilter | "";
  audience: number | null;
}

export function filterSources(sources: readonly SourceView[], filters: SourceFilters, labelOf: (code: number) => string): SourceView[] {
  const needle = filters.query.trim().toLowerCase();
  return sources.filter((source) => {
    if (filters.tab !== "all" && kindOf(source) !== filters.tab) return false;
    if (filters.type && typeOf(source) !== filters.type) return false;
    if (filters.audience !== null && !source.audiences.includes(filters.audience)) return false;
    if (!needle) return true;
    const haystack = [source.title, source.url ?? "", source.status, source.status_reason ?? "", ...source.audiences.map(labelOf)].join(" ");
    return haystack.toLowerCase().includes(needle);
  });
}

export interface KnowledgeSummary {
  total: number;
  counts: Record<SourceTab, number>;
  chunks: number;
  excluded: number;
  uploadedBytes: number;
  /** Completion time of the latest finished sync of any source (never a per-page time). */
  lastSyncAt: string | null;
  /** Sources whose sync failed. */
  attention: SourceView[];
  /** Ready sources whose last sync had pages that could not be processed. */
  warnings: SourceView[];
  /** Sources whose sync has not finished yet. */
  syncing: number;
}

function latest(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

export function summarize(sources: readonly SourceView[]): KnowledgeSummary {
  let chunks = 0;
  let excluded = 0;
  let uploadedBytes = 0;
  let lastSyncAt: string | null = null;
  for (const source of sources) {
    chunks += source.chunk_count;
    excluded += source.excluded_count;
    uploadedBytes += source.size_bytes ?? 0;
    lastSyncAt = latest(lastSyncAt, source.last_synced_at ?? null);
  }
  const attention = sources.filter((s) => syncPhase(s) === "failed");
  const warnings = sources.filter((s) => syncPhase(s) === "warnings");
  const syncing = sources.filter((s) => syncPhase(s) === "processing").length;
  return { total: sources.length, counts: tabCounts(sources), chunks, excluded, uploadedBytes, lastSyncAt, attention, warnings, syncing };
}

/** Coloured tile for the name cell (mirrors the mockup: PDF red, sheet green, Word brand…). */
export function sourceIcon(source: Pick<SourceView, "type" | "file_type">): { icon: IconName; tile: string } {
  const type = typeOf(source);
  if (type === "pdf") return { icon: "file-text", tile: "bg-danger-soft text-danger" };
  if (type === "spreadsheet") return { icon: "file-spreadsheet", tile: "bg-success-soft text-success" };
  if (type === "word" || type === "slides") return { icon: "file-text", tile: "bg-primary-soft text-primary-ink" };
  if (type === "image") return { icon: "image", tile: "bg-primary-soft text-primary-ink" };
  if (type === "website") return { icon: "globe", tile: "bg-sunken text-fg-muted" };
  if (type === "qa") return { icon: "message-square", tile: "bg-accent-soft text-accent-ink" };
  return { icon: "quote", tile: "bg-sunken text-fg-muted" };
}

const PUBLIC_CODE = 1;
/** Audience codes are fixed (labels are tenant data), so their badge styling can be keyed by code. */
const AUDIENCE_STYLE: Readonly<Record<number, { tone: BadgeTone; icon: IconName }>> = {
  1: { tone: "brand", icon: "globe" },
  2: { tone: "warning", icon: "users" },
  3: { tone: "success", icon: "graduation-cap" },
  4: { tone: "neutral", icon: "lock" },
};
const FALLBACK_STYLE = { tone: "neutral" as BadgeTone, icon: "users" as IconName };

export function audienceStyle(code: number): { tone: BadgeTone; icon: IconName } {
  return AUDIENCE_STYLE[code] ?? FALLBACK_STYLE;
}

/** Most open audience first: a public source shows "Public"; otherwise its lowest code + "+n". */
export function primaryAudience(audiences: readonly number[]): { code: number; extra: number } | null {
  if (audiences.length === 0) return null;
  if (audiences.includes(PUBLIC_CODE)) return { code: PUBLIC_CODE, extra: 0 };
  const sorted = [...audiences].sort((a, b) => a - b);
  return { code: sorted[0] ?? PUBLIC_CODE, extra: sorted.length - 1 };
}

export function pageOf<T>(rows: readonly T[], page: number): T[] {
  const start = page * PAGE_SIZE;
  return rows.slice(start, start + PAGE_SIZE);
}

const MEMBERS_CODE = 3;
/** Add-source tabs (`files`, `website`, `qa`, `text`, …): where a new source starts out. */
export type AddSourceTab = "files" | "website" | "qa" | "text" | "drive";

/**
 * Starting access of a new source. A website is public by nature (Everyone); files, Q&A and text
 * snippets start private (Signed-in users) so nothing uploaded reaches guests unless somebody
 * chooses it (DEC-067 — the server defaults the same way when a request names no access).
 */
export function defaultAudiencesFor(tab: AddSourceTab): number[] {
  return tab === "website" ? [PUBLIC_CODE] : [MEMBERS_CODE];
}

/** Everyone (guests included) is chosen for something that is not a website: warn about personal data. */
export function exposesToGuests(tab: AddSourceTab, audiences: readonly number[]): boolean {
  return tab !== "website" && tab !== "drive" && audiences.includes(PUBLIC_CODE);
}
